import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Image as ImageIcon, Upload } from "lucide-react";

function resolveApiUrl() {
  const envApiUrl = import.meta.env.VITE_API_URL as string | undefined;
  const fallback = `${window.location.protocol}//${window.location.hostname}:4000`;
  if (!envApiUrl) return fallback;
  try {
    const u = new URL(envApiUrl);
    if (u.hostname === "localhost" || u.hostname === "127.0.0.1") return fallback;
    return envApiUrl;
  } catch {
    return envApiUrl || fallback;
  }
}

const MALAYSIA_TZ = "Asia/Kuala_Lumpur";
function malaysiaDateKeyFromDate(d: Date) {
  const fmt = new Intl.DateTimeFormat("en-CA", {
    timeZone: MALAYSIA_TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  const parts = fmt.formatToParts(d);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "";
  return `${get("year")}-${get("month")}-${get("day")}`;
}

function formatDateKeyLabel(dateKey: string) {
  const [y, m, d] = dateKey.split("-").map(Number);
  if (!y || !m || !d) return dateKey;
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: MALAYSIA_TZ,
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(new Date(Date.UTC(y, m - 1, d)));
}

function formatDateTime(iso: string | null | undefined) {
  if (!iso) return "-";
  try {
    return new Intl.DateTimeFormat("en-GB", {
      timeZone: MALAYSIA_TZ,
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(iso));
  } catch {
    return "-";
  }
}

type SalesProduct = {
  productId: string;
  name: string;
  sku: string | null;
  qtySold: number;
  tradeInQty?: number;
  sellingPrice: number | null;
  reimbursement?: number | null;
  rebate?: number | null;
  value: number | null;
  tradeInValue?: number | null;
  tradeInLineValue?: number | null;
};
type SalesGroup = { groupName: string; products: SalesProduct[] };
type TeamSalesResponse = {
  team: { id: string; name: string } | null;
  groups: SalesGroup[];
};
type BankSlip = { id: string; imageUrl: string; s3Key: string; createdAt?: string };
type LocalBankSlip = { localId: string; file: File; previewUrl: string };
type PendingPayload = {
  salesDate: string;
  lines: Array<{ productId: string; qtySold: number; tradeInQty?: number }>;
  groups: SalesGroup[];
};
type PaymentRecord = {
  id: string;
  amount: number;
  remark: string | null;
  createdAt: string;
  bankSlips: BankSlip[];
};

const SALES_PENDING_CONFIRM_KEY = "salesPendingConfirmPayload";
const ALLOWED_IMAGE_MIME_TYPES = new Set(["image/jpeg", "image/png", "image/gif", "image/webp"]);

/** Shrink phone-camera bank slips so uploads stay under nginx/proxy body limits (~1MB default). */
async function compressImageForUpload(file: File, maxEdgePx = 1600, quality = 0.72): Promise<File> {
  if (!file.type.startsWith("image/") || file.type === "image/gif") return file;
  // Already small enough — skip work.
  if (file.size <= 900_000) return file;

  const bitmap = await createImageBitmap(file);
  try {
    const scale = Math.min(1, maxEdgePx / Math.max(bitmap.width, bitmap.height));
    const width = Math.max(1, Math.round(bitmap.width * scale));
    const height = Math.max(1, Math.round(bitmap.height * scale));
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) return file;
    ctx.drawImage(bitmap, 0, 0, width, height);

    const blob: Blob | null = await new Promise((resolve) =>
      canvas.toBlob((b) => resolve(b), "image/jpeg", quality)
    );
    if (!blob || blob.size >= file.size) return file;

    const baseName = file.name.replace(/\.[^.]+$/, "") || "bank-slip";
    return new File([blob], `${baseName}.jpg`, { type: "image/jpeg", lastModified: Date.now() });
  } finally {
    bitmap.close();
  }
}

function formatAtmAmountFromDigits(digits: string) {
  const onlyDigits = digits.replace(/\D/g, "");
  const normalized = onlyDigits.length ? onlyDigits : "0";
  const value = Number(normalized) / 100;
  return value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatMoney(v: number) {
  return `RM ${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// Shared "select image -> local preview grid" UI used both for the first submission
// and for a top-up payment. Uploads only happen when the surrounding form is confirmed.
function SlipPicker({
  localSlips,
  onSelectImage,
  onRemove,
  onPreview,
  brokenKeys,
  onImageError,
}: {
  localSlips: LocalBankSlip[];
  onSelectImage: (file: File) => void;
  onRemove: (localId: string) => void;
  onPreview: (url: string) => void;
  brokenKeys: Record<string, boolean>;
  onImageError: (localId: string) => void;
}) {
  return (
    <div>
      <label className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-slate-700 cursor-pointer hover:bg-gray-50">
        <Upload size={16} />
        <span>Select Image</span>
        <input
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) onSelectImage(file);
            e.currentTarget.value = "";
          }}
        />
      </label>

      {localSlips.length ? (
        <div className="mt-3 grid grid-cols-3 gap-2">
          {localSlips.map((s) => (
            <div key={s.localId} className="relative rounded-lg overflow-hidden border border-gray-200 bg-white">
              <button
                type="button"
                className="absolute top-1 right-1 z-20 rounded-full bg-black/70 text-white w-6 h-6 flex items-center justify-center text-sm"
                onClick={() => onRemove(s.localId)}
                aria-label="Remove selected image"
              >
                ×
              </button>
              <button
                type="button"
                className="block w-full"
                onClick={() => onPreview(s.previewUrl)}
                aria-label="Open full image preview"
              >
                {brokenKeys[s.localId] ? (
                  <div className="w-full h-24 bg-gray-50 flex items-center justify-center text-[11px] text-slate-500 px-2 text-center">
                    Preview unavailable
                  </div>
                ) : (
                  <img
                    src={s.previewUrl}
                    alt="Selected bank slip preview"
                    className="w-full h-24 object-contain bg-gray-50"
                    onError={() => onImageError(s.localId)}
                  />
                )}
              </button>
            </div>
          ))}
        </div>
      ) : (
        <div className="mt-3 text-sm text-slate-500 flex items-center gap-2">
          <ImageIcon size={16} />
          <span>No bank slip uploaded yet.</span>
        </div>
      )}
    </div>
  );
}

export default function SalesConfirm() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const apiUrl = useMemo(() => resolveApiUrl(), []);
  const token = localStorage.getItem("token") || "";

  const [mode, setMode] = useState<"submit" | "payment" | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [sales, setSales] = useState<TeamSalesResponse | null>(null);
  const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(null);
  const [brokenPreviewKeys, setBrokenPreviewKeys] = useState<Record<string, boolean>>({});

  // Submit mode (first-time submission for the day).
  const [pending, setPending] = useState<PendingPayload | null>(null);
  const [localBankSlips, setLocalBankSlips] = useState<LocalBankSlip[]>([]);
  const [bankInAmountDigits, setBankInAmountDigits] = useState("");
  const [remark, setRemark] = useState("");
  const [saving, setSaving] = useState(false);
  const [showMismatchConfirm, setShowMismatchConfirm] = useState(false);
  const [mismatchDiffCents, setMismatchDiffCents] = useState(0);

  // Payment (top-up) mode: sales already submitted for today.
  const [bankedAt, setBankedAt] = useState<string | null>(null);
  const [remainingAmount, setRemainingAmount] = useState(0);
  const [paymentHistory, setPaymentHistory] = useState<PaymentRecord[]>([]);
  const [paymentLocalSlips, setPaymentLocalSlips] = useState<LocalBankSlip[]>([]);
  const [paymentAmountDigits, setPaymentAmountDigits] = useState("");
  const [paymentRemark, setPaymentRemark] = useState("");

  // The Sales page passes along the day being entered (any of the last 7 days) so this page
  // resolves proof/payment data against that sales date.
  const dateParam = searchParams.get("date");
  const todayKey = useMemo(() => dateParam || malaysiaDateKeyFromDate(new Date()), [dateParam]);
  const isFullySettled = remainingAmount <= 0.004;

  const totals = useMemo(() => {
    let totalQty = 0;
    let totalValue = 0;
    let reimbursement = 0;
    let rebate = 0;
    for (const g of sales?.groups ?? []) {
      for (const p of g.products ?? []) {
        const qty = p.qtySold ?? 0;
        const price = typeof p.sellingPrice === "number" ? p.sellingPrice : 0;
        const reimb = typeof p.reimbursement === "number" ? Math.max(0, p.reimbursement) : 0;
        const reb = typeof p.rebate === "number" ? Math.max(0, p.rebate) : 0;
        const afterReimb = Math.max(0, price - reimb);
        totalQty += qty;
        totalValue += qty * price;
        reimbursement += qty * Math.min(reimb, Math.max(0, price));
        rebate += qty * Math.min(reb, afterReimb);
      }
    }
    return {
      totalQty,
      totalValue,
      reimbursement,
      rebate,
      totalPayment: Math.max(0, totalValue - reimbursement - rebate),
    };
  }, [sales]);

  const loadPaymentModeData = useCallback(async (): Promise<boolean> => {
    if (!token) {
      setError("Your login has expired. Please log out and log in again.");
      return false;
    }
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [salesRes, proofRes] = await Promise.all([
        fetch(`${apiUrl}/supervisor/team-sales?date=${todayKey}`, { headers }),
        fetch(`${apiUrl}/supervisor/team-sales/bank-proof-today?salesDate=${todayKey}`, { headers }),
      ]);
      const salesJson: any = await salesRes.json().catch(() => ({}));
      const proofJson: any = await proofRes.json().catch(() => ({}));

      if (!proofRes.ok) {
        setError(proofJson?.message || `Failed to load submission (${proofRes.status}).`);
        return false;
      }
      if (!proofJson?.sales?.bankedAt) {
        setError("No sales submission found for this date. Please submit sales from the Sales page first.");
        return false;
      }

      setSales({ team: salesJson?.team ?? null, groups: salesJson?.groups ?? [] });
      setBankedAt(proofJson.sales.bankedAt);
      const remaining = Number(proofJson.sales.remainingAmount ?? 0);
      setRemainingAmount(Number.isFinite(remaining) ? Math.max(0, remaining) : 0);
      setPaymentHistory(Array.isArray(proofJson.sales.payments) ? proofJson.sales.payments : []);
      const remainingCents = Math.max(0, Math.round((Number.isFinite(remaining) ? remaining : 0) * 100));
      setPaymentAmountDigits(remainingCents > 0 ? String(remainingCents) : "");
      return true;
    } catch {
      setError("Unable to connect to server. Please try again.");
      return false;
    }
  }, [apiUrl, token, todayKey]);

  useEffect(() => {
    const load = async () => {
      if (!token) {
        setError("Your login has expired. Please log out and log in again.");
        return;
      }

      const rawPending = sessionStorage.getItem(SALES_PENDING_CONFIRM_KEY);
      if (rawPending) {
        setMode("submit");
        const parsedPending = JSON.parse(rawPending) as PendingPayload;
        setPending(parsedPending);
        setSales({ team: null, groups: parsedPending.groups });
        return;
      }

      setMode("payment");
      setLoading(true);
      setError("");
      try {
        await loadPaymentModeData();
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, [token, loadPaymentModeData]);

  useEffect(() => {
    return () => {
      localBankSlips.forEach((s) => URL.revokeObjectURL(s.previewUrl));
      paymentLocalSlips.forEach((s) => URL.revokeObjectURL(s.previewUrl));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelectImage = (setter: React.Dispatch<React.SetStateAction<LocalBankSlip[]>>) => (file: File) => {
    if (!file) return;
    if (!ALLOWED_IMAGE_MIME_TYPES.has(file.type)) {
      setError("Unsupported image format. Please use JPG, PNG, GIF, or WEBP.");
      setSuccess("");
      return;
    }
    setError("");
    const previewUrl = URL.createObjectURL(file);
    setter((prev) => [{ localId: `${Date.now()}-${Math.random()}`, file, previewUrl }, ...prev]);
  };

  const removeLocalSlip = (setter: React.Dispatch<React.SetStateAction<LocalBankSlip[]>>) => (localId: string) => {
    setter((prev) => {
      const target = prev.find((s) => s.localId === localId);
      if (target) {
        URL.revokeObjectURL(target.previewUrl);
        if (previewImageUrl === target.previewUrl) setPreviewImageUrl(null);
      }
      return prev.filter((s) => s.localId !== localId);
    });
    setBrokenPreviewKeys((prev) => {
      const next = { ...prev };
      delete next[localId];
      return next;
    });
  };

  const uploadLocalSlips = async (slips: LocalBankSlip[]): Promise<Array<{ imageUrl: string; s3Key: string }>> => {
    const uploaded: Array<{ imageUrl: string; s3Key: string }> = [];
    for (const slip of slips) {
      const fileToUpload = await compressImageForUpload(slip.file);
      const formData = new FormData();
      formData.append("image", fileToUpload);
      const uploadRes = await fetch(`${apiUrl}/supervisor/team-sales/upload-bank-slip-today`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const uploadJson: any = await uploadRes.json().catch(() => ({}));
      if (!uploadRes.ok) {
        if (uploadRes.status === 413) {
          throw new Error(
            "Bank slip image is too large for the server. Please retake a smaller photo or compress the image, then try again."
          );
        }
        throw new Error(uploadJson?.message || `Upload failed (${uploadRes.status}).`);
      }
      const created = uploadJson?.data;
      if (created?.imageUrl && created?.s3Key) {
        uploaded.push({ imageUrl: created.imageUrl, s3Key: created.s3Key });
      }
    }
    return uploaded;
  };

  const doSaveBankIn = async () => {
    if (!token) return;
    if (!pending?.lines?.length) {
      setError("No staged sales lines found. Please submit from Sales page again.");
      return;
    }
    const amount = Number(bankInAmountDigits || "0") / 100;
    if (!Number.isFinite(amount) || amount <= 0) {
      setError("Please enter a valid Bank In Amount.");
      return;
    }
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      const uploadedLocalSlips = await uploadLocalSlips(localBankSlips);

      const res = await fetch(`${apiUrl}/supervisor/team-sales/confirm-bank-in-today`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          bankInAmount: amount,
          remark: remark.trim() ? remark.trim() : undefined,
          salesDate: pending.salesDate,
          lines: pending.lines.map((l) => ({
            productId: l.productId,
            qtySold: l.qtySold,
            tradeInQty: l.tradeInQty ?? 0,
          })),
          bankSlips: uploadedLocalSlips,
        }),
      });
      const json: any = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(json?.message || `Save failed (${res.status}).`);
        return;
      }
      sessionStorage.removeItem(SALES_PENDING_CONFIRM_KEY);
      setLocalBankSlips((prev) => {
        prev.forEach((s) => URL.revokeObjectURL(s.previewUrl));
        return [];
      });
      window.dispatchEvent(new Event("sales-outstanding-changed"));
      navigate("/app/sales/success");
    } catch (e: any) {
      setError(e?.message || "Unable to save bank-in proof. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveBankIn = async () => {
    const amount = Number(bankInAmountDigits || "0") / 100;
    if (!Number.isFinite(amount) || amount <= 0) {
      setError("Please enter a valid Bank In Amount.");
      return;
    }
    if (!localBankSlips.length) {
      setError("Please attach at least one bank slip / transfer slip image.");
      return;
    }
    const totalCents = Math.round((totals.totalPayment || 0) * 100);
    const amountCents = Math.round(amount * 100);
    const diff = amountCents - totalCents;

    // Only warn when there is a real difference (>= 1 cent).
    if (diff !== 0) {
      setMismatchDiffCents(diff);
      setShowMismatchConfirm(true);
      return;
    }

    await doSaveBankIn();
  };

  const doSubmitPayment = async () => {
    if (!token) return;
    const amount = Number(paymentAmountDigits || "0") / 100;
    if (!Number.isFinite(amount) || amount <= 0) {
      setError("Please enter a valid payment amount.");
      return;
    }
    if (!paymentLocalSlips.length) {
      setError("Please attach at least one bank slip / transfer slip image.");
      return;
    }
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      const uploadedLocalSlips = await uploadLocalSlips(paymentLocalSlips);

      const res = await fetch(`${apiUrl}/supervisor/team-sales/add-payment-today`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          amount,
          remark: paymentRemark.trim() ? paymentRemark.trim() : undefined,
          salesDate: todayKey,
          bankSlips: uploadedLocalSlips,
        }),
      });
      const json: any = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(json?.message || `Save failed (${res.status}).`);
        return;
      }

      // Re-fetch from the server (rather than reconstructing locally) so the payment history
      // shows properly signed, viewable slip image URLs instead of raw/private S3 URLs.
      await loadPaymentModeData();
      setPaymentLocalSlips((prev) => {
        prev.forEach((s) => URL.revokeObjectURL(s.previewUrl));
        return [];
      });
      setPaymentRemark("");
      const data = json?.data ?? {};
      const nextRemainingCents = Math.max(0, Math.round(Number(data.remainingAmount ?? 0) * 100));
      setSuccess(nextRemainingCents > 0 ? "Payment recorded successfully." : "Payment recorded. This submission is now fully settled.");
      window.dispatchEvent(new Event("sales-outstanding-changed"));
    } catch (e: any) {
      setError(e?.message || "Unable to save payment. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const formatCentsToRM = (cents: number) => {
    const abs = Math.abs(cents);
    const v = abs / 100;
    return `RM ${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const remainingCentsForCap = Math.max(0, Math.round(remainingAmount * 100));

  return (
    <div className="min-h-[calc(100vh-60px)] bg-gray-50">
      <div className="h-[100px] bg-gradient-to-r from-theme-1/95 to-theme-2/95 text-white relative">
        <div aria-hidden className="absolute inset-0 bg-texture-white opacity-20 pointer-events-none" />
        <div className="relative z-10 h-full px-4 pt-3">
          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={() => navigate("/app/sales")}
              className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center"
              aria-label="Back"
            >
              <ArrowLeft size={24} className="text-white" />
            </button>
            <div className="text-[18px] font-semibold">Sales Submission Summary</div>
            <div className="w-10 h-10" />
          </div>
        </div>
      </div>

      <div className="px-4 py-3 pb-28">
        {error ? <div className="mb-3 rounded-lg border border-red-200 bg-red-50 text-red-700 px-3 py-2 text-sm">{error}</div> : null}
        {success ? <div className="mb-3 rounded-lg border border-green-200 bg-green-50 text-green-700 px-3 py-2 text-sm">{success}</div> : null}

        <div className="rounded-xl border border-gray-200 bg-white p-3 mb-3">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-semibold text-slate-900">Sales Summary</div>
            <div className="text-xs text-slate-500">
              {formatDateKeyLabel((mode === "submit" ? pending?.salesDate : todayKey) || todayKey)}
            </div>
          </div>
          {loading ? (
            <div className="text-sm text-slate-500">Loading...</div>
          ) : (
            <>
              {(sales?.groups ?? []).map((g) => (
                <div key={g.groupName} className="mb-2">
                  <div className="text-base font-bold text-slate-900 mb-1">{g.groupName}</div>
                  {(g.products ?? [])
                    .filter((p) => (p.qtySold ?? 0) > 0 || (p.tradeInQty ?? 0) > 0)
                    .map((p) => (
                      <div key={p.productId} className="flex items-center justify-between text-sm py-0.5 gap-2">
                        <div className="min-w-0 truncate">{p.name}</div>
                        <div className="shrink-0 text-right tabular-nums text-slate-700">
                          {(p.qtySold ?? 0) > 0 ? <div>{p.qtySold}</div> : null}
                          {(p.tradeInQty ?? 0) > 0 ? (
                            <div className="text-xs text-cyan-700">TI {p.tradeInQty}</div>
                          ) : null}
                        </div>
                      </div>
                    ))}
                </div>
              ))}

              <div className="mt-2 pt-2 border-t border-gray-200 flex items-start justify-between">
                <div>
                  <div className="text-xs text-slate-500">Total Quantity Sold</div>
                  <div className="text-base font-semibold text-slate-900 tabular-nums">{totals.totalQty.toLocaleString()}</div>
                </div>
                <div className="text-right space-y-1">
                  <div>
                    <div className="text-xs text-slate-500">Total Value (RM)</div>
                    <div className="text-sm font-semibold text-slate-900 tabular-nums">{formatMoney(totals.totalValue)}</div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-500">− Reimbursement</div>
                    <div className="text-sm font-semibold text-slate-900 tabular-nums">{formatMoney(totals.reimbursement)}</div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-500">− Rebate</div>
                    <div className="text-sm font-semibold text-slate-900 tabular-nums">{formatMoney(totals.rebate)}</div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-500">Total Payment</div>
                    <div className="text-base font-semibold text-slate-900 tabular-nums">{formatMoney(totals.totalPayment)}</div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {mode === "payment" ? (
          <>
            <div className="rounded-xl border border-gray-200 bg-white p-3 mb-3">
              <div className="flex items-center justify-between mb-1">
                <div className="text-sm font-semibold text-slate-900">Payment Status</div>
                <div className="text-xs text-slate-500">Submitted {formatDateTime(bankedAt)}</div>
              </div>
              {isFullySettled ? (
                <div className="rounded-lg border border-green-200 bg-green-50 text-green-800 px-3 py-2 text-sm font-medium">
                  Fully settled — no further payment required.
                </div>
              ) : (
                <div className="rounded-lg border border-amber-200 bg-amber-50 text-amber-800 px-3 py-2 text-sm font-medium">
                  Amount still owed: <span className="font-bold tabular-nums">{formatMoney(remainingAmount)}</span>
                </div>
              )}
            </div>

            <div className="rounded-xl border border-gray-200 bg-white p-3 mb-3">
              <div className="text-sm font-semibold text-slate-900 mb-2">Payment History</div>
              {paymentHistory.length === 0 ? (
                <div className="text-sm text-slate-500">No payments recorded yet.</div>
              ) : (
                <div className="space-y-3">
                  {paymentHistory.map((p, idx) => (
                    <div key={p.id || idx} className="rounded-lg border border-gray-100 bg-gray-50 p-2.5">
                      <div className="flex items-center justify-between text-sm">
                        <div className="font-semibold text-slate-900 tabular-nums">{formatMoney(p.amount)}</div>
                        <div className="text-xs text-slate-500">{formatDateTime(p.createdAt)}</div>
                      </div>
                      {p.bankSlips?.length || p.remark ? (
                        <div className="mt-2 flex items-start gap-3">
                          {p.bankSlips?.length ? (
                            <div className="flex flex-wrap gap-1.5 shrink-0">
                              {p.bankSlips.map((s) => (
                                <button
                                  key={s.id}
                                  type="button"
                                  className="block w-20 h-20 shrink-0 rounded-md overflow-hidden border border-gray-200 bg-white"
                                  onClick={() => setPreviewImageUrl(s.imageUrl)}
                                  aria-label="Open full image preview"
                                >
                                  {brokenPreviewKeys[s.id] ? (
                                    <div className="w-full h-full bg-gray-50 flex items-center justify-center text-[9px] text-slate-500">
                                      N/A
                                    </div>
                                  ) : (
                                    <img
                                      src={s.imageUrl}
                                      alt="Bank slip"
                                      className="w-full h-full object-contain bg-gray-50"
                                      onError={() => setBrokenPreviewKeys((prev) => ({ ...prev, [s.id]: true }))}
                                    />
                                  )}
                                </button>
                              ))}
                            </div>
                          ) : null}
                          {p.remark ? <div className="min-w-0 text-xs text-slate-600 pt-1">{p.remark}</div> : null}
                        </div>
                      ) : null}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {!isFullySettled ? (
              <div className="rounded-xl border border-gray-200 bg-white p-3">
                <div className="text-sm font-semibold text-slate-900 mb-2">Add Payment</div>

                <SlipPicker
                  localSlips={paymentLocalSlips}
                  onSelectImage={handleSelectImage(setPaymentLocalSlips)}
                  onRemove={removeLocalSlip(setPaymentLocalSlips)}
                  onPreview={setPreviewImageUrl}
                  brokenKeys={brokenPreviewKeys}
                  onImageError={(localId) => setBrokenPreviewKeys((prev) => ({ ...prev, [localId]: true }))}
                />

                <div className="mt-4">
                  <div className="text-sm font-semibold text-slate-900 mb-1">Remark</div>
                  <textarea
                    value={paymentRemark}
                    onChange={(e) => setPaymentRemark(e.target.value)}
                    rows={3}
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-slate-900 outline-none"
                  />
                </div>

                <div className="mt-4">
                  <div className="text-sm font-semibold text-slate-900 mb-1">Payment Amount</div>
                  <input
                    type="text"
                    inputMode="numeric"
                    className="w-1/2 rounded-lg border border-gray-200 px-3 py-2 text-sm text-slate-900 outline-none tabular-nums"
                    placeholder="0.00"
                    value={formatAtmAmountFromDigits(paymentAmountDigits)}
                    onChange={(e) => {
                      const digitsOnly = e.target.value.replace(/\D/g, "");
                      const cents = digitsOnly === "" ? 0 : Number(digitsOnly);
                      const capped = Math.min(cents, remainingCentsForCap);
                      setPaymentAmountDigits(capped > 0 ? String(capped) : "");
                    }}
                  />
                  <div className="mt-1 text-xs text-slate-500">Maximum: {formatMoney(remainingAmount)}</div>
                </div>

                <button
                  type="button"
                  onClick={() => void doSubmitPayment()}
                  disabled={saving}
                  className="mt-4 w-full rounded-xl bg-primary text-white py-3 text-sm font-semibold disabled:opacity-60"
                >
                  {saving ? "Saving..." : "Submit Payment"}
                </button>
              </div>
            ) : null}
          </>
        ) : null}

        {mode === "submit" ? (
          <div className="rounded-xl border border-gray-200 bg-white p-3">
            <div className="text-sm font-semibold text-slate-900 mb-2">Bank Slip / Transfer Slip</div>

            <SlipPicker
              localSlips={localBankSlips}
              onSelectImage={handleSelectImage(setLocalBankSlips)}
              onRemove={removeLocalSlip(setLocalBankSlips)}
              onPreview={setPreviewImageUrl}
              brokenKeys={brokenPreviewKeys}
              onImageError={(localId) => setBrokenPreviewKeys((prev) => ({ ...prev, [localId]: true }))}
            />

            <div className="mt-4">
              <div className="text-sm font-semibold text-slate-900 mb-1">Remark</div>
              <textarea
                value={remark}
                onChange={(e) => setRemark(e.target.value)}
                rows={3}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-slate-900 outline-none"
              />
            </div>

            <div className="mt-4">
              <div className="text-sm font-semibold text-slate-900 mb-1">Bank In Amount</div>
              <input
                type="text"
                inputMode="numeric"
                className="w-1/2 rounded-lg border border-gray-200 px-3 py-2 text-sm text-slate-900 outline-none tabular-nums"
                placeholder="0.00"
                value={formatAtmAmountFromDigits(bankInAmountDigits)}
                onChange={(e) => {
                  const digitsOnly = e.target.value.replace(/\D/g, "");
                  setBankInAmountDigits(digitsOnly);
                }}
              />
            </div>

            <button
              type="button"
              onClick={() => void handleSaveBankIn()}
              disabled={saving}
              className="mt-4 w-full rounded-xl bg-primary text-white py-3 text-sm font-semibold disabled:opacity-60"
            >
              {saving ? "Saving..." : "Submit Sales"}
            </button>
          </div>
        ) : null}
      </div>

      {previewImageUrl ? (
        <button
          type="button"
          className="fixed inset-0 z-[120] bg-black/75 p-4 flex items-start justify-center"
          onClick={() => setPreviewImageUrl(null)}
          aria-label="Close image preview"
        >
          <img
            src={previewImageUrl}
            alt="Full bank slip preview"
            className="max-w-full max-h-full object-contain rounded-lg bg-white"
          />
        </button>
      ) : null}

      {showMismatchConfirm ? (
        <div className="fixed inset-0 z-[130] bg-black/60 flex items-start justify-center p-4">
          <div className="w-full max-w-md rounded-2xl bg-white shadow-xl overflow-hidden">
            <div className="p-4 border-b border-gray-100">
              <div className="text-base font-semibold text-slate-900">Amount mismatch</div>
              <div className="text-sm text-slate-600 mt-1">
                Your <span className="font-semibold">Bank In Amount</span> does not match <span className="font-semibold">Total Payment</span>.
              </div>
            </div>
            <div className="p-4 space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-600">Total Value (RM)</span>
                <span className="font-semibold tabular-nums text-slate-900">{formatMoney(totals.totalValue)}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-600">− Reimbursement</span>
                <span className="font-semibold tabular-nums text-slate-900">{formatMoney(totals.reimbursement)}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-600">− Rebate</span>
                <span className="font-semibold tabular-nums text-slate-900">{formatMoney(totals.rebate)}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-600">Total Payment</span>
                <span className="font-semibold tabular-nums text-slate-900">{formatMoney(totals.totalPayment)}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-600">Bank In Amount</span>
                <span className="font-semibold tabular-nums text-slate-900">
                  {formatAtmAmountFromDigits(bankInAmountDigits)}
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-600">Difference</span>
                <span
                  className={`font-bold tabular-nums ${mismatchDiffCents === 0 ? "text-slate-900" : mismatchDiffCents > 0 ? "text-green-700" : "text-red-700"}`}
                >
                  {mismatchDiffCents > 0 ? `+${formatCentsToRM(mismatchDiffCents)}` : `-${formatCentsToRM(mismatchDiffCents)}`}
                </span>
              </div>
              <div className="text-xs text-slate-500 mt-2">
                If you proceed, this difference will be saved for audit. You can record additional bank-in payments
                later from the Sales page.
              </div>
            </div>
            <div className="p-4 border-t border-gray-100 flex gap-2">
              <button
                type="button"
                className="flex-1 rounded-xl border border-gray-200 bg-white py-2.5 text-sm font-semibold text-slate-700"
                onClick={() => setShowMismatchConfirm(false)}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="button"
                className="flex-1 rounded-xl bg-primary text-white py-2.5 text-sm font-semibold disabled:opacity-60"
                onClick={() => {
                  setShowMismatchConfirm(false);
                  void doSaveBankIn();
                }}
                disabled={saving}
              >
                Proceed
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
